import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { CarouselIcon } from '~/components/CommonCarousel';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';
import { userStore } from '~/stores';
import { Category } from '~/stores/CategoriesStore';

import CategoryBlockTitle from '../CategoryBlockTitle';
import { cfSrcForImage, createCategoryUrl } from '../utils';

import styles from './CategoriesCarouselSliderThreeWBlock.module.scss';

interface CategoriesCarouselSliderThreeWBlockProps {
  category: Category;
}

const CategoriesCarouselSliderThreeWBlock = ({
  category,
}: CategoriesCarouselSliderThreeWBlockProps) => {
  const { handleClickCategory } = useCategoryAnalytics();
  const { isMobile } = useGlobal();
  const [api, setApi] = useState<CarouselApi | undefined>(undefined);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  const subcategoryLength = category.subcategory.length;

  const showArrows = !isMobile && subcategoryLength > 1;

  const carouselOpts = useMemo(
    () => ({
      align: 'start' as const,
      slidesToScroll: isMobile ? 3 : 4,
      containScroll: 'trimSnaps' as const,
      dragFree: isMobile,
    }),
    [],
  );

  if (!category.subcategory.length || !category.subcategory[0]?.id) {
    return null;
  }

  return (
    <div className={classes([styles.container])}>
      <CategoryBlockTitle
        text={category.name}
        className={styles.title}
        categoryId={category.id}
        linkTo={createCategoryUrl({ id: category.id })}
      />
      <div
        className={classes([
          styles.desktopContainer,
          'banner-carousel carousel',
        ])}
      >
        {!prevBtnDisabled && showArrows && (
          <CarouselIcon
            position="left"
            variant="transparent"
            onClick={onPrevButtonClick}
          />
        )}
        <Carousel
          setApi={setApi}
          opts={carouselOpts}
          className={styles.items}
          dir={userStore.dir}
          key={userStore.dir}
        >
          <CarouselContent
            style={{
              gap: isMobile ? '20px' : '8px',
            }}
          >
            {category.subcategory.map(
              ({ name, previewImage, id, previewText }) => (
                <CarouselItem
                  key={name}
                  className={styles.item}
                  style={{
                    backgroundImage: `url('${encodeURI(
                      cfSrcForImage(previewImage || '', {
                        width: 285,
                        height: 380,
                      }),
                    )}')`,
                  }}
                >
                  <Link
                    data-link={`category-${id}`}
                    onClick={handleClickCategory(id)}
                    to={createCategoryUrl({ id })}
                    className={styles.link}
                  >
                    <div className={styles.itemTitle}>{name}</div>
                    <div
                      className={styles.itemText}
                      dangerouslySetInnerHTML={{
                        __html: previewText,
                      }}
                    />
                  </Link>
                </CarouselItem>
              ),
            )}
          </CarouselContent>
        </Carousel>
        {!nextBtnDisabled && showArrows && (
          <CarouselIcon
            position="right"
            variant="transparent"
            onClick={onNextButtonClick}
          />
        )}
      </div>
    </div>
  );
};

export default observer(CategoriesCarouselSliderThreeWBlock);
